import React, { useEffect, useState } from "react";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Loader2, Landmark } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

// Creditor attaches a verified repayment account to a specific "owed to me" debt.
export default function AttachRepaymentSheet({ open, onOpenChange, debt, onDone }) {
  const [banks, setBanks] = useState([]);
  const [profile, setProfile] = useState(null);
  const [bankCode, setBankCode] = useState("");
  const [acctNum, setAcctNum] = useState("");
  const [resolved, setResolved] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setBankCode(""); setAcctNum(""); setResolved(null);
    api.get("/repayment/banks").then((r) => setBanks(r.data.banks || [])).catch(() => {});
    api.get("/repayment/account").then((r) => setProfile(r.data.account)).catch(() => setProfile(null));
    document.body.style.pointerEvents = "";
  }, [open]);

  const bankName = banks.find((b) => b.code === bankCode)?.name || "";

  const verify = async () => {
    setVerifying(true); setResolved(null);
    try {
      const { data } = await api.post("/repayment/resolve", { bank_code: bankCode, account_number: acctNum });
      setResolved(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setVerifying(false);
    }
  };

  const attach = async (payload, label) => {
    setSaving(true);
    try {
      await api.post(`/debts/${debt.id}/repayment-account`, payload);
      toast.success("Repayment account added");
      onOpenChange(false);
      onDone && onDone();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[92dvh]">
        <div className="mx-auto w-full max-w-md px-5 pb-8 pt-2 overflow-y-auto overscroll-contain" data-testid="attach-repayment-sheet">
          <div className="flex items-center gap-2.5">
            <div className="h-10 w-10 rounded-xl bg-secondary flex items-center justify-center"><Landmark className="h-5 w-5 text-primary" /></div>
            <div>
              <h2 className="font-display text-lg font-bold text-foreground">Add repayment account</h2>
              <p className="text-xs text-muted-foreground">Where {debt?.person_name} can repay you.</p>
            </div>
          </div>

          {profile && profile.verified && (
            <button
              data-testid="attach-use-saved"
              disabled={saving}
              onClick={() => attach({ use_profile: true })}
              className="mt-5 w-full rounded-2xl border border-primary/30 bg-secondary p-4 text-left hover:bg-secondary/70 transition-colors"
            >
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Use my saved account</p>
              <p className="font-display font-bold text-foreground">{profile.account_name}</p>
              <p className="text-sm text-muted-foreground">{profile.bank_name} {profile.account_masked}</p>
            </button>
          )}

          <p className="mt-5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            {profile && profile.verified ? "Or use a different account" : "Bank account"}
          </p>

          <div className="mt-2 space-y-3">
            <Select value={bankCode} onValueChange={(v) => { setBankCode(v); setResolved(null); }}>
              <SelectTrigger data-testid="attach-bank-select" className="h-12 rounded-xl"><SelectValue placeholder="Select bank" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {banks.map((b) => (<SelectItem key={`${b.code}-${b.name}`} value={b.code}>{b.name}</SelectItem>))}
              </SelectContent>
            </Select>
            <Input
              data-testid="attach-account-input"
              inputMode="numeric"
              maxLength={10}
              placeholder="10-digit account number"
              value={acctNum}
              onChange={(e) => { setAcctNum(e.target.value.replace(/\D/g, "").slice(0, 10)); setResolved(null); }}
              className="h-12 rounded-xl text-lg"
            />

            {!resolved && (
              <Button data-testid="attach-verify-btn" disabled={!bankCode || acctNum.length !== 10 || verifying} onClick={verify} className="w-full h-12 rounded-xl">
                {verifying ? <Loader2 className="h-5 w-5 animate-spin" /> : "Verify account"}
              </Button>
            )}

            {resolved && (
              <div data-testid="attach-resolved" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                <p className="flex items-center gap-1.5 text-sm font-semibold text-emerald-800"><CheckCircle2 className="h-4 w-4" /> Account found</p>
                <p className="mt-2 font-display text-lg font-bold text-foreground">{resolved.account_name}</p>
                <p className="text-sm text-muted-foreground">{bankName} {resolved.account_masked}</p>
                <p className="mt-3 text-sm text-foreground">Is this the correct account?</p>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <Button data-testid="attach-confirm-save" disabled={saving} onClick={() => attach({ bank_code: bankCode, bank_name: bankName, account_number: acctNum })} className="h-11 rounded-xl">Confirm account</Button>
                  <Button variant="outline" disabled={saving} onClick={() => setResolved(null)} className="h-11 rounded-xl">Edit</Button>
                </div>
              </div>
            )}
          </div>

          <p className="mt-4 text-[11px] text-muted-foreground text-center">
            Debii never receives or holds your money. Payment is made directly to you.
          </p>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
